'use client'
import React, { useState } from 'react'
import { ModalClose } from '@/components/ui/modal'
import { Button } from '@/components/ui/button';


interface FormData {
    name: string;
    email: string;
    organization: string;
    phone: string;
    priority: string;
    problem: string;
}

const INPUT_FIELDS = [
    { name: 'name', type: 'text', placeholder: 'Full Name', required: true },
    { name: 'email', type: 'email', placeholder: 'Email Address', required: true },
    { name: 'organization', type: 'text', placeholder: 'Company Name', required: true },
    { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: false },
    { name: 'priority', type: 'text', placeholder: 'immediate / exploratory / planned', required: true },
]

const HubspotForm = () => {
    const [isLoading, setIsLoading] = useState(false)

    const formatHubspotData = (formData: FormData) => ({
        fields: [
            { name: "firstname", value: formData.name },
            { name: "company", value: formData.organization },
            { name: "problem", value: formData.problem },
            { name: "priority", value: formData.priority },
            { name: "email", value: formData.email },
            { name: "phone", value: formData.phone },
        ],
        legalConsentOptions: {
            consent: {
                consentToProcess: true,
                text: "I agree to allow Strivelabs.ai to store and process my personal data.",
                communications: [
                    {
                        value: true,
                        subscriptionTypeId: 999,
                        text: "I agree to receive marketing communications from Strivelabs.ai.",
                    },
                ],
            },
        },
    })

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setIsLoading(true)

        const formData = new FormData(event.target as HTMLFormElement)
        const formEntries = Object.fromEntries(formData) as Record<string, string>
        const data = formatHubspotData({
            name: formEntries.name,
            email: formEntries.email,
            organization: formEntries.organization,
            phone: formEntries.phone,
            priority: formEntries.priority,
            problem: formEntries.problem
        })

        try {
            const response = await fetch(
                `https://api.hsforms.com/submissions/v3/integration/submit/${process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID}/${process.env.NEXT_PUBLIC_HUBSPOT_FORM_ID}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(data),
                }
            )

            if (response.ok) {

                (event.target as HTMLFormElement).reset()
            } else {
                throw new Error("Failed to send message.")
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false)
        }
    }

    const inputClassName = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#c6c6c6] focus:shadow-[0_0_0_2px_rgb(255,255,255),0_0_0_4px_#FFB673,0_1px_2px_0px_rgba(0,0,0,0.05)] transition-all font-inter text-gray-800"

    return (
        <div className="flex flex-col p-6 w-[400px] max-md:w-full">
            <h2 className="text-3xl mb-6 bg-gradient-to-r from-[#FF9D2E] to-[#A02AE4] bg-clip-text text-transparent font-bold text-center">Let&apos;s Connect</h2>
            <form className="space-y-4" onSubmit={handleSubmit}>
                {INPUT_FIELDS.map((field) => (
                    <div key={field.name}>
                        <input
                            type={field.type}
                            name={field.name}
                            placeholder={field.placeholder}
                            required={field.required}
                            className={inputClassName}
                        />
                    </div>
                ))}
                <div>
                    <textarea
                        name="problem"
                        placeholder="Tell us about your requirements"
                        rows={4}
                        required
                        className={inputClassName}
                        style={{ resize: 'none' }}
                    />
                </div>
                <div className="flex gap-4 max-sm:flex-col">
                    <Button
                        type="submit"
                        disabled={isLoading}
                        variant='default'
                        className={`w-full bg-primary shadow-lg py-2 px-4 rounded-md relative overflow-hidden group transition-all ease-out duration-300 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        aria-label="send message"
                    >
                        <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-1000 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:-translate-x-40 ease"></span>
                        <span className="relative text-white">
                            {isLoading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </div>
                            ) : (
                                'Submit'
                            )}
                        </span>
                    </Button>
                    <ModalClose className="w-3/4 inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-white text-gray-600 hover:text-accent-foreground h-11 rounded-md px-8 border-gray-300 hover:bg-gray-200">
                        Cancel
                    </ModalClose>
                </div>
            </form>
        </div>
    )
}

export default HubspotForm